import React, { useState, useEffect } from 'react';
import { listStaff } from '../../services/staff';
import { assignStaff as assignLicenceStaff } from '../../services/licences';

// Generic assign staff modal that can work for both licences and quotations.
// If `onAssign` is provided, it will be used; otherwise it falls back to the
// default licence assign API.
const AssignStaffModal = ({ isOpen, onClose, licenceId, onAssign }) => {
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [selectedApprover, setSelectedApprover] = useState('');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && licenceId) {
      loadStaff();
    }
  }, [isOpen, licenceId]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await listStaff({ page: 1, limit: 100 });
      setStaff(response.data || []);
    } catch (err) {
      console.error('Failed to load staff:', err);
      alert('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  // Filter staff by role
  const reviewers = staff.filter(member => {
    const roles = member.roleIds || member.roles || [];
    return roles.some(role => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return roleName == 'Reviewer'|| roleName == 'reviewer';
    });
  });

  const approvers = staff.filter(member => {
    const roles = member.roleIds || member.roles || [];
    return roles.some(role => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return roleName == 'Approver' || roleName == 'approver';
    });
  });

  const handleSubmit = async () => {
    if (!selectedReviewer || !selectedApprover || !licenceId) return;
    
    try {
      setSubmitting(true);
      if (typeof onAssign === 'function') {
        // Let the parent handle the API call (e.g. quotations)
        await onAssign(selectedReviewer, selectedApprover);
      } else {
        // Default behaviour: assign staff to a licence
        await assignLicenceStaff(licenceId, {
          reviewerId: selectedReviewer,
          approverId: selectedApprover
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to assign staff:', err);
      alert(`Failed to assign staff: ${err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl">
        {/* Modal Content */}
        <div className="p-8">
          {/* Header */}
          <div className="flex flex-col mb-8">
            <div className="flex justify-end mb-4">
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <h2 className="text-[28px] font-semibold text-[#0a1f17] font-inter tracking-[-0.56px] leading-[1.4]">
              Assign Staff
            </h2>
          </div>

          {/* Form Content */}
          <div className="space-y-6 mb-8">
            {/* Select Reviewer */}
            <div className="flex flex-col gap-2">
              <label className="text-[14px] text-[#485b54] font-poppins leading-[1.7] font-medium">
                Select Reviewer
              </label>
              <div className="relative">
                <select
                  value={selectedReviewer}
                  onChange={(e) => setSelectedReviewer(e.target.value)}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-white border border-[#ebebeb] rounded-lg text-[14px] text-[#73827c] font-poppins leading-[1.7] appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all disabled:opacity-50"
                >
                  <option value="">Select</option>
                  {reviewers.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim()}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Select Approver */}
            <div className="flex flex-col gap-2">
              <label className="text-[14px] text-[#485b54] font-poppins leading-[1.7] font-medium">
                Select Approver
              </label>
              <div className="relative">
                <select
                  value={selectedApprover}
                  onChange={(e) => setSelectedApprover(e.target.value)}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-white border border-[#ebebeb] rounded-lg text-[14px] text-[#73827c] font-poppins leading-[1.7] appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all disabled:opacity-50"
                >
                  <option value="">Select</option>
                  {approvers.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim()}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 h-11 border border-gray-300 text-gray-700 text-[14px] font-medium font-poppins rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedReviewer || !selectedApprover || submitting || loading}
              className="px-6 py-3 h-11 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white text-[14px] font-medium font-poppins rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {submitting ? 'Assigning...' : 'Assign Staff'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignStaffModal;

